import idb from "@data/idb";
import { User } from "@models/user";
import logEvent from "analytics";
import EventBus from "eventBus";
import { useAsyncValState } from "hooks/util/useAsyncValState";
import { useEffect } from "react";

export type DotID =
  | "background_setting"
  | "beta_screen"
  | "generate_cards_menu_item"
  | "generate_cards_deck_screen_menu"
  | "generate_cards_modal_suggestions"
  | "inbox_body"
  | "inbox_deck_select_modal"
  | "japanese_furigana_override"
  | "cards_per_session_setting"
  | "review_tags_setting"
  | "review_font_size_setting"
  | "review_timer_setting"
  | "review_orientation_setting"
  | "review_mode_setting"
  | "flip_stabilization_setting"
  | "fsrs_retention_setting"
  | "theater_mode_menu_item"
  | "theater_mode_review_screen_menu";

// When you add a dot, record what date it was introduced on.
// This lets us hide dots after the user has had enough chance to see them.
const dotIntroducedAt: Record<DotID, Date> = {
  background_setting: new Date("1970-01-01"),
  beta_screen: new Date("1970-01-01"),
  generate_cards_menu_item: new Date("1970-01-01"),
  generate_cards_deck_screen_menu: new Date("1970-01-01"),
  generate_cards_modal_suggestions: new Date("1970-01-01"),
  inbox_body: new Date("1970-01-01"),
  inbox_deck_select_modal: new Date("1970-01-01"),
  japanese_furigana_override: new Date("1970-01-01"),
  cards_per_session_setting: new Date("1970-01-01"),
  review_tags_setting: new Date("1970-01-01"),
  review_font_size_setting: new Date("1970-01-01"),
  review_timer_setting: new Date("1970-01-01"),
  review_orientation_setting: new Date("1970-01-01"),
  review_mode_setting: new Date("1970-01-01"),
  flip_stabilization_setting: new Date("1970-01-01"),
  fsrs_retention_setting: new Date("1970-01-01"),
  theater_mode_menu_item: new Date("1970-01-01"),
  theater_mode_review_screen_menu: new Date("1970-01-01"),
};

export async function clearDot(id: DotID): Promise<void> {
  const tx = idb.dots.transaction(["dots"], "readwrite");
  const store = tx.objectStore("dots");
  const dot = await store.get(id);
  if (!dot || !dot.clearedAt) {
    await store.put({ ...dot, id, clearedAt: new Date() });
    EventBus.emit("dotCleared", { dotID: id });
    logEvent("dot_funnel", { dotID: id, step: "cleared" });
  }
  await tx.done;
}

export async function recordDotImpression(id: DotID): Promise<void> {
  const tx = idb.dots.transaction(["dots"], "readwrite");
  const store = tx.objectStore("dots");
  const dot = await store.get(id);
  if (dot?.firstSeenAt) {
    return;
  }
  await store.put({ ...dot, id, firstSeenAt: new Date() });
  await tx.done;
  logEvent("dot_funnel", { dotID: id, step: "first_impression" });
}

export async function getClearedAt(id: DotID): Promise<Date | undefined> {
  const row = await idb.dots.get("dots", id);
  return row?.clearedAt;
}

export function dotExpired(id: DotID): boolean {
  const expirationDays = 14;
  const now = Date.now();
  const dotAgeDays = Math.floor(now - dotIntroducedAt[id].getTime()) / (1000 * 60 * 60 * 24);
  const userAgeDays = User.accountAgeDays();
  if (!userAgeDays) {
    return false;
  }
  return dotAgeDays > expirationDays && userAgeDays > expirationDays;
}

export function useClearedAtAndRecordImpression(id: DotID | undefined) {
  const [clearedAt, setClearedAt, setError] = useAsyncValState<Date | undefined>();
  useEffect(() => {
    if (!id) {
      return;
    }

    recordDotImpression(id).catch(() => {
      // Don't care.
    });

    function update({ dotID }: { dotID: DotID }) {
      if (id !== dotID) {
        return;
      }
      getClearedAt(id).then(setClearedAt).catch(setError);
    }

    update({ dotID: id });
    EventBus.on("dotCleared", update);
    return () => {
      EventBus.off("dotCleared", update);
    };
  }, [id, setClearedAt, setError]);
  return clearedAt;
}

export function useClearDot(id: DotID) {
  useEffect(() => {
    clearDot(id).catch(() => {});
  }, [id]);
}
